import Grid from './Grid';
import Tile from './Tile';
import EventTarget from './EventTarget';
import HTMLActuator from './HTMLActuator';
import LocalStorageManager from './LocalStorageManager';
import KeyboardInputManager from './KeyboardInputManager';
import {mkE} from "./DOM";

export default class GameManager extends EventTarget {

	EVENT_START_GAME = 'start_game';
	EVENT_RESTART = 'restart';
	EVENT_KEEP_PLAYING = 'keep_playing';
	EVENT_GAME_OVER = 'game_over';
	EVENT_UPDATE_SCORE = 'update_score';

	startTiles = 2;

	score = 0;

	/**
	 *
	 * @param {Number} size
	 */
	constructor( size = 4 ) {
		super();

		this.size = size;

		this.node = mkE({
			tag: 'div',
			className: 'game2048',
			els: [
				this.messageContainer = mkE({
					tag: 'div',
					className: 'game-message',
					els: [
						{
							tag: 'p'
						},
						{
							tag: 'div',
							className: 'lower',
							els: [
								{
									tag: 'a',
									className: 'keep-playing-button',
									text: 'Turpināt spēlēt',
									onclick: this.keepPlaying.bind(this)
								},
								{
									tag: 'a',
									className: 'retry-button',
									text: 'Mēģināt vēlreiz',
									onclick: this.restart.bind(this)
								}
							]
						}
					]
				}),
				{
					tag: 'div',
					className: 'grid-container',
					els: (() => {
						let rows = [];
						for( let i = 0; i < this.size; i++ ) {
							let cells = [];
							for( let j = 0; j < this.size; j++ ) {
								cells.push({
									tag: 'div',
									className: 'grid-cell'
								});
							}
							rows.push({
								tag: 'div',
								className: 'grid-row',
								els: cells
							});
						}
						return rows;
					})()
				},
				this.tileContainer = mkE({
					tag: 'div',
					className: 'tile-container'
				})
			]
		});

		this.inputManager = new KeyboardInputManager(this.node);
		this.inputManager.addEventListener('move', this.move.bind(this));
		this.storageManager = new LocalStorageManager;
		this.actuator = new HTMLActuator(this.tileContainer, this.messageContainer);
	}

	/** @param {HTMLElement} node */
	append( node ) {
		node.append(this.node);
		return this;
	}

	// Restart the game
	restart() {
		this.storageManager.clearGameState();
		this.actuator.continueGame(); // Clear the game won/lost message
		this.dispatchEvent(this.EVENT_RESTART, {score: this.score});
		this.setup();
	};

	// Keep playing after winning (allows going over 2048)
	keepPlaying() {
		this.keepPlaying = true;
		this.actuator.continueGame(); // Clear the game won/lost message
		this.dispatchEvent(this.EVENT_KEEP_PLAYING);
	};

	// Return true if the game is lost, or has won and the user hasn't kept playing
	isGameTerminated() {
		return this.over || (this.won && !this.keepPlaying);
	};

	// Set up the game
	setup() {
		let previousState = this.storageManager.getGameState();

		// Reload the game from a previous game if present
		if( previousState ) {
			this.grid = new Grid(previousState.grid.size, previousState.grid.cells); // Reload grid
			this.score = previousState.score;
			this.over = previousState.over;
			this.won = previousState.won;
			this.keepPlaying = previousState.keepPlaying;
		} else {
			this.grid = new Grid(this.size);
			this.score = 0;
			this.over = false;
			this.won = false;
			this.keepPlaying = false;

			// Add the initial tiles
			this.addStartTiles();
		}

		this.dispatchEvent(this.EVENT_START_GAME);

		// Update the actuator
		this.actuate();

		return this;
	};

	getScore() {
		return this.score;
	}

	getBestScore() {
		return this.storageManager.getBestScore();
	}

	// Set up the initial tiles to start the game with
	addStartTiles() {
		for( let i = 0; i < this.startTiles; i++ ) {
			this.addRandomTile();
		}
	};

	// Adds a tile in a random position
	addRandomTile() {
		if( this.grid.cellsAvailable() ) {
			let value = Math.random() < 0.9 ? 2 : 4;
			let tile = new Tile(this.grid.randomAvailableCell(), value);

			this.grid.insertTile(tile);
		}
	};

	// Sends the updated grid to the actuator
	actuate() {
		if( this.storageManager.getBestScore() < this.score ) {
			this.storageManager.setBestScore(this.score);
			this.dispatchEvent(this.EVENT_UPDATE_SCORE, this.score, true);
		} else {
			this.dispatchEvent(this.EVENT_UPDATE_SCORE, this.score, false);
		}

		// Clear the state when the game is over (game over only, not win)
		if( this.over ) {
			this.storageManager.clearGameState();
			this.dispatchEvent(this.EVENT_GAME_OVER, {score: this.score});
		} else {
			this.storageManager.setGameState(this.serialize());
		}

		this.actuator.actuate(this.grid, {
			score: this.score,
			over: this.over,
			won: this.won,
			bestScore: this.storageManager.getBestScore(),
			terminated: this.isGameTerminated()
		});

	};

	// Represent the current game as an object
	serialize() {
		return {
			grid: this.grid.serialize(),
			score: this.score,
			over: this.over,
			won: this.won,
			keepPlaying: this.keepPlaying
		};
	};

	// Save all tile positions and remove merger info
	prepareTiles() {
		this.grid.eachCell(function ( x, y, tile ) {
			if( tile ) {
				tile.mergedFrom = null;
				tile.savePosition();
			}
		});
	};

	// Move a tile and its representation
	moveTile( tile, cell ) {
		this.grid.cells[tile.x][tile.y] = null;
		this.grid.cells[cell.x][cell.y] = tile;
		tile.updatePosition(cell);
	};

	// Move tiles on the grid in the specified direction
	move( direction ) {
		// 0: up, 1: right, 2: down, 3: left

		// Don't do anything if the game's over
		if( this.isGameTerminated() ) {
			return;
		}

		let cell, tile;

		let vector = this.getVector(direction);
		let traversals = this.buildTraversals(vector);
		let moved = false;

		// Save the current tile positions and remove merger information
		this.prepareTiles();

		// Traverse the grid in the right direction and move tiles
		traversals.x.forEach(( x ) => {
			traversals.y.forEach(( y ) => {
				cell = {x: x, y: y};
				tile = this.grid.cellContent(cell);

				if( tile ) {
					let positions = this.findFarthestPosition(cell, vector);
					let next = this.grid.cellContent(positions.next);

					// Only one merger per row traversal?
					if( next && next.value === tile.value && !next.mergedFrom ) {
						let merged = new Tile(positions.next, tile.value * 2);
						merged.mergedFrom = [tile, next];

						this.grid.insertTile(merged);
						this.grid.removeTile(tile);

						// Converge the two tiles' positions
						tile.updatePosition(positions.next);

						// Update the score
						this.score += merged.value;

						// The mighty 2048 tile
						if( merged.value === 2048 ) this.won = true;
					} else {
						this.moveTile(tile, positions.farthest);
					}

					if( !this.positionsEqual(cell, tile) ) {
						moved = true; // The tile moved from its original cell!
					}
				}
			});
		});

		if( moved ) {
			this.addRandomTile();

			if( !this.movesAvailable() ) {
				this.over = true; // Game over!
			}

			this.actuate();
		}
	};

	// Get the vector representing the chosen direction
	getVector( direction ) {
		// Vectors representing tile movement
		let map = {
			0: {x: 0, y: -1}, // Up
			1: {x: 1, y: 0},  // Right
			2: {x: 0, y: 1},  // Down
			3: {x: -1, y: 0}   // Left
		};

		return map[direction];
	};

	// Build a list of positions to traverse in the right order
	buildTraversals( vector ) {
		let traversals = {x: [], y: []};

		for( let pos = 0; pos < this.size; pos++ ) {
			traversals.x.push(pos);
			traversals.y.push(pos);
		}

		// Always traverse from the farthest cell in the chosen direction
		if( vector.x === 1 ) traversals.x = traversals.x.reverse();
		if( vector.y === 1 ) traversals.y = traversals.y.reverse();

		return traversals;
	};

	findFarthestPosition( cell, vector ) {
		let previous;

		// Progress towards the vector direction until an obstacle is found
		do {
			previous = cell;
			cell = {x: previous.x + vector.x, y: previous.y + vector.y};
		} while( this.grid.withinBounds(cell) &&
		this.grid.cellAvailable(cell) );

		return {
			farthest: previous,
			next: cell // Used to check if a merge is required
		};
	};

	movesAvailable() {
		return this.grid.cellsAvailable() || this.tileMatchesAvailable();
	};

	// Check for available matches between tiles (more expensive check)
	tileMatchesAvailable() {
		let tile;

		for( let x = 0; x < this.size; x++ ) {
			for( let y = 0; y < this.size; y++ ) {
				tile = this.grid.cellContent({x: x, y: y});

				if( tile ) {
					for( let direction = 0; direction < 4; direction++ ) {
						let vector = this.getVector(direction);
						let cell = {x: x + vector.x, y: y + vector.y};

						let other = this.grid.cellContent(cell);

						if( other && other.value === tile.value ) {
							return true; // These two tiles can be merged
						}
					}
				}
			}
		}

		return false;
	};

	positionsEqual( first, second ) {
		return first.x === second.x && first.y === second.y;
	};

}